import { useCallback, useEffect, useMemo, useState } from "react";
import "./MarketMonitoring.css";
import { Exchange, Markets } from "./DemoExchangeData";
import Select from "react-select";
import { Filter, Product } from "../../../store/state/MarketMontitoring/types";
import { filters } from "./Filters";
import { COLOR_CODES } from "../../../common";
import {
  FormatComparativeByMarketProductData,
  FormatMarketMonitoringData,
} from "./FormatData";
import { ChartType, LegendKey } from "../../../models/chart_model";
import { buildFormDataRequest } from "../../../Rest_api/Network_utility";
import { searchStyle } from "../../../components/Search";
import {
  lightenColor,
  ReMixChart,
} from "../../../components/recharts/ReMixCharts";
import { stringToConsistentColor } from "../../../extensions/string";
import { MarketAnalysis } from "./Analysis/MarketAnalysis";
import { DynamicDatePicker } from "../../../components/DatePicker";
import Tooltip from "../../../components/tooltip";
import FilterMenu from "../../../components/FilterMenu";
import MainPageContainer from "../../../components/MainPageContainer";
import NewFilterContainer from "../../../components/NewFilterContainer ";
import { PageHeader } from "../../../components/Page";
import Hamburger from "../../../components/Hamburger";
import SecondaryHeader from "../../Rajasthan/Demand/SecondaryHeader";
import LegendSummaryTable, {
  ChartTable,
} from "../../../components/table/SummaryTable";
import { useDispatch, useSelector } from "react-redux";
import { setShowTabs } from "../../../store/state/SwitchTabs";
import { useLegendFilter } from "../../../hooks/useLegendFilter";
import { MetricChipHead } from "../../../components/Metrics";
import {
  fetchMarketMonitoringMetrics,
  MarketMonitoringMetrics,
} from "../../../Rest_api/RestApi";
import { ReactComponent as AvgIcon } from "../../../icons/AvgIcon.svg";
import { ReactComponent as TotalIcon } from "../../../icons/TotalIcon.svg";
import Loading, { ChartAreaLoader } from "../../../components/Loading";
import { ReactComponent as GNAiIcon } from "../../../icons/gnai_logo.svg";

// ─── constants ────────────────────────────────────────────────────────────────
const VIEW_TYPES = ["Cumulative", "Comparative"];

const ANALYSIS_INTERVAL = [
  { label: "Date", key: "date" },
  { label: "Product", key: "product" },
  { label: "Exchange", key: "exchange" },
];
const ANALYSIS_EXCHANGES = ["IEX", "HPX", "PXIL", "DEEP"];
const ANALYSIS_PRODUCTS = [
  "DAC",
  "DAM",
  "Daily",
  "GDAM",
  "HP DAC",
  "HP Daily",
  "HP Intra Day",
  "HP Monthly",
  "HP Weekly",
  "Intra day",
  "Monthly",
  "RA",
  "RTM",
  "Weekly",
];
const ANALYSIS_GROUP_BY = ["exchange", "product", "type"];

const BY_VOLUME_KEYS: LegendKey[] = [
  { dataKey: "dam", name: "DAM", api_key: "DAM", stroke: COLOR_CODES.dam },
  { dataKey: "rtm", name: "RTM", api_key: "RTM", stroke: COLOR_CODES.rtm },
  {
    dataKey: "hpdam",
    name: "HPDAM",
    api_key: "HPDAM",
    stroke: COLOR_CODES.hpdAm,
  },
  { dataKey: "gdam", name: "GDAM", api_key: "GDAM", stroke: COLOR_CODES.gdam },
  {
    dataKey: "intraDay",
    name: "Intra Day",
    api_key: "Intra-Day Contracts",
    stroke: COLOR_CODES.intraday,
  },
  {
    dataKey: "contingencyContracts",
    name: "DAC",
    api_key: "Day Ahead Contingency Contracts",
    stroke: COLOR_CODES.dac,
  },
  {
    dataKey: "daily",
    name: "Daily",
    api_key: "Daily Contracts",
    stroke: COLOR_CODES.daily,
  },
  {
    dataKey: "weekly",
    name: "Weekly",
    api_key: "Weekly Contracts",
    stroke: COLOR_CODES.weekly,
  },
  {
    dataKey: "monthly",
    name: "Monthly",
    api_key: "Monthly Contracts",
    stroke: COLOR_CODES.monthly,
  },
  {
    dataKey: "singleSided",
    name: "Any Day S.S",
    api_key: "Any Day Single Sided Contracts",
    stroke: COLOR_CODES.anydaySSC,
  },
  {
    dataKey: "bilateral",
    name: "Bilateral",
    api_key: "Bilateral",
    stroke: COLOR_CODES.bilateral,
  },
];

// ─── component ────────────────────────────────────────────────────────────────
export function MarketMontoring() {
  const dispatch = useDispatch();
  const dataTab = useSelector((state: any) => state.viewMode.activeView);
  const selectedTab: string = "Comparision";

  const [startMonth, setStartMonth] = useState<{
    value: number;
    label: string;
  }>({ value: -1, label: "" });
  const [endMonth, setEndMonth] = useState<{ value: number; label: string }>({
    value: -1,
    label: "",
  });
  const [priceData, setPriceData] = useState<any[]>([]);
  const [tabIndex, setTabIndex] = useState(0);
  const [metrics, setMetrics] = useState<MarketMonitoringMetrics | null>(null);
  const [selectedMarket, setSelectedMarket] = useState<Filter[]>(Markets);
  const [selectedExchange, setSelectedExchange] = useState<Filter[]>(Exchange);
  const [dateRange, setDateRange] = useState(() => {
    const today = new Date();
    const oneMonthAgo = new Date();
    oneMonthAgo.setMonth(today.getMonth() - 1);
    return [{ startDate: oneMonthAgo, endDate: today, key: "selection" }];
  });

  // ─── analysis filter state ────────────────────────────────────────────────
  const [analysisExchange, setAnalysisExchange] = useState<string[]>([]);
  const [analysisProduct, setAnalysisProduct] = useState<string[]>([]);
  const [analysisGroupBy, setAnalysisGroupBy] = useState<string[]>(["type"]);
  const [analysisInterval, setAnalysisInterval] = useState("date");

  const availableAnalysisGroupBy = ANALYSIS_GROUP_BY.filter(
    (v) => !analysisInterval.includes(v),
  ).filter(
    (v) =>
      !(
        v === "type" &&
        (analysisInterval.includes("product") ||
          analysisInterval.includes("exchange"))
      ),
  );

  const handleAnalysisIntervalChange = (_: string, index: number) => {
    const key = ANALYSIS_INTERVAL[index].key;
    setAnalysisInterval(key);
    setAnalysisGroupBy(key === "exchange" ? ["product"] : ["exchange"]);
  };

  useEffect(() => {
    dispatch(setShowTabs(false));
    return () => {
      dispatch(setShowTabs(true));
    };
  }, [dispatch]);

  const [selectedByPriceProductIds, setSelectedByPriceProductIds] = useState<
    number[]
  >(
    getDefaultByPriceSelection(
      getProducts({ exchange: Exchange, market: Markets }),
    ),
  );

  // ─── derived data ─────────────────────────────────────────────────────────
  const products = useMemo(
    () => getProducts({ exchange: selectedExchange, market: selectedMarket }),
    [selectedExchange, selectedMarket],
  );

  const selectedByPriceProducts = useMemo(() => {
    const selected = products.filter((item) =>
      selectedByPriceProductIds.includes(item.id),
    );

    if (selected.length > 0) {
      return selected;
    }

    const defaultIds = getDefaultByPriceSelection(products);
    return products.filter((item) => defaultIds.includes(item.id));
  }, [products, selectedByPriceProductIds]);

  useEffect(() => {
    if (products.length === 0) {
      setSelectedByPriceProductIds([]);
      return;
    }

    setSelectedByPriceProductIds((prev) => {
      const availableIds = products.map((item) => item.id);
      const retained = prev.filter((id) => availableIds.includes(id));
      return retained.length > 0
        ? retained
        : getDefaultByPriceSelection(products);
    });
  }, [products]);

  const [filterLoading, setFilterLoading] = useState<boolean>(false);
  const [marketMonitoringChartData, setMarketMonitoringChartData] = useState<
    any[]
  >([]);
  const [comparativeSeriesResult, setComparativeSeriesResult] = useState<any>({
    rows: [],
    seriesMeta: [],
  });

  useEffect(() => {
    setFilterLoading(true);
    const timeoutId = setTimeout(() => {
      const cumulativeData = FormatMarketMonitoringData(
        priceData,
        selectedExchange.map((e) => e.name),
        [...selectedMarket.map((e) => e.name), "Traders", "Bilateral"],
        true,
      );
      setMarketMonitoringChartData(cumulativeData);

      const comparativeData = FormatComparativeByMarketProductData(
        priceData,
        [
          ...selectedByPriceProducts.map((item) => item.name),
          "Trading Licensees",
        ],
        [...selectedMarket.map((e) => e.name), "Traders"],
        selectedExchange.map((item) => item.name),
        true,
      );
      setComparativeSeriesResult(comparativeData);
      setFilterLoading(false);
    }, 0);
    return () => clearTimeout(timeoutId);
  }, [priceData, selectedExchange, selectedMarket, selectedByPriceProducts]);

  const [isFetchingData, setIsFetchingData] = useState<boolean>(false);

  const byPriceChartData = comparativeSeriesResult.rows;

  // interleaved: [DAM Vol, DAM Price, RTM Vol, RTM Price, ...]
  const cumulativeLegends = useMemo(
    () =>
      [...BY_VOLUME_KEYS]
        .filter((e) => products.some((p) => p.dname === (e.name ?? e.dataKey)))
        .sort((a, b) =>
          (a.name ?? a.dataKey).localeCompare(b.name ?? b.dataKey),
        )
        .flatMap((e) => [
          {
            dataKey: e.dataKey + "_volume",
            name: e.name,
            stroke: e.stroke as string,
            yAxisId: "right",
            type: ChartType.Bar,
            unit: "MU",
          },
          {
            dataKey: e.dataKey,
            name: e.name + " Price",
            stroke: lightenColor(e.stroke as string, -25),
            unit: "Rs/KWh",
          },
        ]),
    [products],
  );

  // interleaved: [HPX Vol, HPX Price, IEX Vol, ...]
  const comparativeLegends = useMemo(
    () =>
      comparativeSeriesResult.seriesMeta.flatMap((series: any) => {
        const knownProduct = BY_VOLUME_KEYS.find(
          (k) => (k.name ?? k.dataKey) === series.product,
        );
        const lineColor =
          (knownProduct?.stroke as string) ??
          stringToConsistentColor(series.dataKey);
        return [
          {
            dataKey: series.dataKey + "_volume",
            name: series.label,
            type: ChartType.Bar,
            stroke: undefined,
            yAxisId: "right",
            unit: "MU",
          },
          {
            dataKey: series.dataKey,
            name: series.label + " Price",
            stroke: undefined,
            unit: "Rs/KWh",
          },
        ];
      }),
    [comparativeSeriesResult.seriesMeta],
  );

  // ─── fetch ────────────────────────────────────────────────────────────────
  const fetchPriceData = useCallback(
    async (
      overrides: Partial<{
        market: Filter[];
        startMonth: { value: number; label: string };
        endMonth: { value: number; label: string };
      }> = {},
    ) => {
      setIsFetchingData(true);
      const mkt = overrides.market ?? selectedMarket;
      const sm = overrides.startMonth ?? startMonth;
      const em = overrides.endMonth ?? endMonth;

      const byPrice = await buildFormDataRequest({
        endpoint: "data/market_monitoring_price_api",
        method: "POST",
        body: {
          exchange: "HPX,IEX,PXIL,Traders",
          market: mkt.map((item) => item.name).toString(),
          product:
            BY_VOLUME_KEYS.map((item) => item.api_key).toString() +
            ",Trading Licensees",
          start_month: sm.label,
          end_month: em.label,
        },
      });
      setPriceData(byPrice);
      setIsFetchingData(false);
    },
    [selectedMarket, startMonth, endMonth],
  );

  const fetchMetrics = useCallback(
    async (
      overrides: Partial<{
        market: Filter[];
        exchange: Filter[];
        products: Product[] | null;
        startMonth: { value: number; label: string };
        endMonth: { value: number; label: string };
      }> = {},
    ) => {
      const mkt = overrides.market ?? selectedMarket;
      const exc = overrides.exchange ?? selectedExchange;
      const prds = overrides.products !== undefined ? overrides.products : null;
      const sm = overrides.startMonth ?? startMonth;
      const em = overrides.endMonth ?? endMonth;

      // Build exchange param from selected exchanges (fallback to all)
      const exchangeParam =
        exc.length > 0
          ? exc.map((e) => e.name).join(",")
          : "HPX,IEX,PXIL,Traders";

      const toApiProduct = (product: Product) =>
        BY_VOLUME_KEYS.find((k) => k.name === (product.dname ?? product.name))
          ?.api_key ?? product.name;

      const productParam =
        prds != null && prds.length > 0
          ? prds.map((product) => toApiProduct(product)).join(",")
          : BY_VOLUME_KEYS.map((item) => item.api_key).join(",") +
            ",Trading Licensees";

      try {
        setIsFetchingData(true);
        const result = await fetchMarketMonitoringMetrics({
          exchange: exchangeParam,
          market: mkt.map((item) => item.name).join(","),
          product: productParam,
          start_month: sm.label || undefined,
          end_month: em.label || undefined,
        });
        setMetrics(result);
      } catch {
        setMetrics(null);
      } finally {
        setIsFetchingData(false);
      }
    },
    [selectedMarket, selectedExchange, startMonth, endMonth],
  );

  useEffect(() => {
    fetchPriceData();
    fetchMetrics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── handlers ─────────────────────────────────────────────────────────────
  const handleStartMonthChange = useCallback(
    (option: { value: number; label: string } | null) => {
      const newStart = option ?? { value: -1, label: "" };
      setStartMonth(newStart);
      fetchPriceData({ startMonth: newStart });
      fetchMetrics({
        startMonth: newStart,
        products: tabIndex === 1 ? selectedByPriceProducts : null,
      });
    },
    [fetchPriceData, fetchMetrics, tabIndex, selectedByPriceProducts],
  );

  const handleEndMonthChange = useCallback(
    (option: { value: number; label: string } | null) => {
      const newEnd = option ?? { value: -1, label: "" };
      setEndMonth(newEnd);
      fetchPriceData({ endMonth: newEnd });
      fetchMetrics({
        endMonth: newEnd,
        products: tabIndex === 1 ? selectedByPriceProducts : null,
      });
    },
    [fetchPriceData, fetchMetrics, tabIndex, selectedByPriceProducts],
  );

  const handleExchangeChange = useCallback(
    (items: string[]) => {
      const newExchange = Exchange.filter((e) => items.includes(e.name));
      setSelectedExchange(newExchange);

      // Compute products that will be available after this exchange change
      const newProducts = getProducts({
        exchange: newExchange,
        market: selectedMarket,
      });

      let validProducts: Product[] | null = null;
      if (tabIndex === 1) {
        const filtered = selectedByPriceProducts.filter((p) =>
          newProducts.some((np) => np.id === p.id),
        );
        validProducts =
          filtered.length > 0
            ? filtered
            : newProducts.filter((p) =>
                getDefaultByPriceSelection(newProducts).includes(p.id),
              );
      }

      fetchMetrics({
        exchange: newExchange,
        products: validProducts,
      });
    },
    [fetchMetrics, tabIndex, selectedByPriceProducts, selectedMarket],
  );

  const handleMarketChange = useCallback(
    (items: string[]) => {
      const newMarket = Markets.filter((m) => items.includes(m.name));
      setSelectedMarket(newMarket);
      fetchPriceData({ market: newMarket });

      // Compute products that will be available after this market change
      const newProducts = getProducts({
        exchange: selectedExchange,
        market: newMarket,
      });

      let validProducts: Product[] | null = null;
      if (tabIndex === 1) {
        const filtered = selectedByPriceProducts.filter((p) =>
          newProducts.some((np) => np.id === p.id),
        );
        validProducts =
          filtered.length > 0
            ? filtered
            : newProducts.filter((p) =>
                getDefaultByPriceSelection(newProducts).includes(p.id),
              );
      }

      fetchMetrics({
        market: newMarket,
        products: validProducts,
      });
    },
    [
      fetchPriceData,
      fetchMetrics,
      tabIndex,
      selectedByPriceProducts,
      selectedExchange,
    ],
  );

  const handleProductSelectionChange = useCallback(
    (items: string[]) => {
      const nextProducts = products.filter((product) =>
        items.includes(product.dname ?? product.name),
      );

      if (nextProducts.length > 0) {
        setSelectedByPriceProductIds(nextProducts.map((item) => item.id));
        fetchMetrics({ products: nextProducts });
        return;
      }

      const defaultIds = getDefaultByPriceSelection(products);
      const defaultProducts = products.filter((item) =>
        defaultIds.includes(item.id),
      );
      setSelectedByPriceProductIds(defaultIds);
      fetchMetrics({ products: defaultProducts });
    },
    [products, fetchMetrics],
  );

  // ─── legend filter hooks ──────────────────────────────────────────────────
  const {
    visibleLegends: visibleCumulativeLegends,
    handleLegendSelect: handleCumulativeLegendSelect,
    selectedLegends: selectedCumulativeLegends,
  } = useLegendFilter(cumulativeLegends);

  const {
    visibleLegends: visibleComparativeLegends,
    handleLegendSelect: handleComparativeLegendSelect,
    selectedLegends: selectedComparativeLegends,
  } = useLegendFilter(comparativeLegends);

  // ─── render ───────────────────────────────────────────────────────────────
  return (
    <div className="page-wrapper">
      <PageHeader
        headerHeight="h-[68px]"
        leftComponent={
          <div className="flex items-center gap-5">
            <GNAiIcon width={80} />
            <Hamburger />
            <h1 className="text-[18px] text-[#6F6D7C]">Market Monitoring</h1>
          </div>
        }
        rightComponent={
          <div className="flex gap-4 items-center">
            {selectedTab === "Comparision" && (
              <>
                <Select
                  placeholder="Start Month"
                  isClearable
                  styles={searchStyle as any}
                  className="dateSelect"
                  value={
                    startMonth.value !== -1
                      ? {
                          value: startMonth.value as any,
                          label: startMonth.label,
                        }
                      : undefined
                  }
                  options={[...filters[2].subfilter].reverse().map((item) => ({
                    value: item.id,
                    label: item.title,
                    isDisabled:
                      endMonth.value !== -1 &&
                      parseInt(item.id.toString()) >
                        parseInt(endMonth.value.toString()),
                  }))}
                  onChange={handleStartMonthChange}
                />
                <Select
                  placeholder="End Month"
                  isClearable
                  styles={searchStyle as any}
                  className="dateSelect"
                  value={
                    endMonth.value !== -1
                      ? { value: endMonth.value as any, label: endMonth.label }
                      : undefined
                  }
                  options={[...filters[2].subfilter].reverse().map((item) => ({
                    value: item.id,
                    label: item.title,
                    isDisabled:
                      parseInt(item.id.toString()) <
                      parseInt(startMonth.value.toString()),
                  }))}
                  onChange={handleEndMonthChange}
                />
              </>
            )}
            {selectedTab === "Analysis" && (
              <DynamicDatePicker
                defaultDates={dateRange[0]}
                setDate={(date) => setDateRange(date)}
              />
            )}
          </div>
        }
      />
      <div className="tabContent">
        <div className="gas-page-container">
          <NewFilterContainer>
            <div className="side-filters-container text-nowrap">
              {selectedTab === "Comparision" && (
                <>
                  <FilterMenu
                    title="Type"
                    items={VIEW_TYPES}
                    selectedItems={[VIEW_TYPES[tabIndex]]}
                    onSelect={(_, index) => {
                      setTabIndex(index);
                      fetchMetrics({
                        products: index === 1 ? selectedByPriceProducts : null,
                      });
                    }}
                  />
                  <FilterMenu
                    title="Market"
                    items={Markets.map((m) => m.name)}
                    selectedItems={selectedMarket.map((m) => m.name)}
                    setSelectedItems={handleMarketChange}
                    disabled={
                      selectedExchange.length === 1 &&
                      selectedExchange[0].name === "Traders"
                    }
                  />
                  <FilterMenu
                    title="Exchange"
                    items={Exchange.map((e) => e.name)}
                    selectedItems={selectedExchange.map((e) => e.name)}
                    setSelectedItems={handleExchangeChange}
                  />
                  <FilterMenu
                    title="Product"
                    items={products.map((p) => p.dname ?? p.name)}
                    selectedItems={selectedByPriceProducts.map(
                      (product) => product.dname ?? product.name,
                    )}
                    setSelectedItems={handleProductSelectionChange}
                    disabled={tabIndex !== 1}
                  />
                </>
              )}
              {selectedTab === "Analysis" && (
                <>
                  <FilterMenu
                    title="X-Axis"
                    items={ANALYSIS_INTERVAL.map((i) => i.label)}
                    selectedItems={[
                      ANALYSIS_INTERVAL.find((i) => i.key === analysisInterval)
                        ?.label ?? "",
                    ]}
                    onSelect={handleAnalysisIntervalChange}
                  />
                  <FilterMenu
                    title="Exchange"
                    items={ANALYSIS_EXCHANGES}
                    selectedItems={analysisExchange}
                    setSelectedItems={setAnalysisExchange}
                    showEmptySelected
                  />
                  <FilterMenu
                    title="Product"
                    items={ANALYSIS_PRODUCTS}
                    selectedItems={analysisProduct}
                    setSelectedItems={setAnalysisProduct}
                    showEmptySelected
                  />
                  <FilterMenu
                    title="Group By"
                    items={availableAnalysisGroupBy}
                    selectedItems={analysisGroupBy}
                    setSelectedItems={setAnalysisGroupBy}
                    showEmptySelected={false}
                  />
                </>
              )}
            </div>
          </NewFilterContainer>

          <MainPageContainer>
            <SecondaryHeader showViewMode />
            {metrics && (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-2 sm:gap-3 my-0 [&>*]:!w-full">
                {Object.entries(metrics.exchanges).map(([name, data]) => (
                  <MetricChipHead
                    key={name}
                    title={name}
                    icon={<AvgIcon width={20} height={19} />}
                    items={[
                      {
                        label: "Wt Avg Price",
                        value: data.weighted_avg_price,
                        unit: "Rs/KWh",
                      },
                      {
                        label: "Vol",
                        value: data.volume_mu,
                        unit: "MU",
                      },
                    ]}
                  />
                ))}
                {/* <MetricChipHead
                title="Overall"
                icon={<TotalIcon width={20} height={19} />}
                items={[
                  {
                    label: "Wt Avg Price",
                    value: metrics.overall.weighted_avg_price,
                    unit: "Rs/KWh",
                  },
                  {
                    label: "Volume",
                    value: metrics.overall.volume_mu,
                    unit: "MU",
                  },
                ]}
              /> */}
              </div>
            )}

            {selectedTab === "Comparision" &&
              (() => {
                const activeData =
                  tabIndex === 0 ? marketMonitoringChartData : byPriceChartData;
                const activeVisibleLegends =
                  tabIndex === 0
                    ? visibleCumulativeLegends
                    : visibleComparativeLegends;
                const activeUnit = tabIndex === 0 ? "MU" : "Rs/KWh";

                const MONTHS = [
                  "Jan",
                  "Feb",
                  "Mar",
                  "Apr",
                  "May",
                  "Jun",
                  "Jul",
                  "Aug",
                  "Sep",
                  "Oct",
                  "Nov",
                  "Dec",
                ];
                const parseMonthOrder = (m: string) => {
                  const [mon, yr] = m.split("-");
                  return parseInt(yr) * 12 + MONTHS.indexOf(mon);
                };

                // negate so table's default "desc" sort gives ascending chronological order
                const sortedData = [...activeData]
                  .map((item) => ({
                    ...item,
                    _monthOrder: -parseMonthOrder(item.month),
                  }))
                  .sort((a, b) => a._monthOrder - b._monthOrder);

                const tablePar = {
                  legends: activeVisibleLegends,
                  data: sortedData,
                  tableData: sortedData,
                  itemsPerPage: 24,
                  unit: activeUnit,
                  extraKeys: ["month"],
                  extraLabels: ["Month"],
                  filename: `Market_Monitoring_${VIEW_TYPES[tabIndex]}`,
                  defaultSortKey: "_monthOrder",
                };

                return (
                  <div
                    className="gas-chart-container"
                    style={{ minHeight: 0, flex: 1 }}
                  >
                    <div className="gas-heading gap-x-10">
                      <h2 className="chartHeading w-60">
                        {VIEW_TYPES[tabIndex]} ({activeUnit})
                      </h2>
                    </div>

                    {filterLoading || isFetchingData ? (
                      <ChartAreaLoader />
                    ) : (
                      <>
                        {dataTab.label === "Chart" && tabIndex === 0 && (
                          <>
                            {/* <div className="flex gap-x-8 justify-center">
                        {LegendComponent(
                          {
                            dataKey: "weighted_avg_rs_per_kwh",
                            stroke: COLOR_CODES.secondary,
                            name: "Wt. Avg Price (Rs/KWh)",
                            yAxisId: "right",
                          },
                          1,
                          [],
                          () => {},
                        )}
                        {LegendComponent(
                          {
                            dataKey: "total_mu",
                            stroke: COLOR_CODES.secondary,
                            name: "Volume (MUs)",
                            type: 1,
                            legendColor: COLOR_CODES.secondary,
                          },
                          0,
                          [],
                          () => {},
                        )}
                      </div> */}
                            <ReMixChart
                              yAxisLabel="Weighted Average Price (Rs/KWh)"
                              secondYAxisWidth={80}
                              showBrush
                              xLabel=""
                              yAxisWidth={50}
                              unit="MUs"
                              data={marketMonitoringChartData as any}
                              brushIndex={{
                                startIndex: Math.max(
                                  0,
                                  marketMonitoringChartData.length - 12,
                                ),
                                endIndex: Math.max(
                                  0,
                                  marketMonitoringChartData.length - 1,
                                ),
                              }}
                              secondYAxisLabel="Volume (MU)"
                              legends={cumulativeLegends}
                              onLegendSelect={handleCumulativeLegendSelect}
                              defaultSelectedLegends={selectedCumulativeLegends}
                              xDataKey="month"
                            />
                          </>
                        )}

                        {dataTab.label === "Chart" && tabIndex === 1 && (
                          <ReMixChart
                            showBrush
                            xLabel=""
                            yAxisLabel="Weighted Average Price (Rs/KWh)"
                            yAxisWidth={80}
                            data={byPriceChartData as any}
                            brushIndex={{
                              startIndex: Math.max(
                                0,
                                byPriceChartData.length - 12,
                              ),
                              endIndex: Math.max(
                                0,
                                byPriceChartData.length - 1,
                              ),
                            }}
                            legends={comparativeLegends}
                            onLegendSelect={handleComparativeLegendSelect}
                            defaultSelectedLegends={selectedComparativeLegends}
                            unit="MUs"
                            xDataKey="month"
                            secondYAxisLabel="Volume (MU)"
                          />
                        )}

                        {dataTab.label === "Summary" && (
                          <LegendSummaryTable
                            tableHeight="calc(100vh - 270px)"
                            {...tablePar}
                          />
                        )}
                        {dataTab.label === "Table" && (
                          <ChartTable
                            tableHeight="calc(100vh - 300px)"
                            {...tablePar}
                          />
                        )}
                      </>
                    )}
                  </div>
                );
              })()}

            {selectedTab === "Analysis" && (
              <MarketAnalysis
                dates={dateRange}
                dataTab={dataTab}
                selectedExchange={analysisExchange}
                selectedProduct={analysisProduct}
                groupBy={analysisGroupBy}
                selectedInterval={analysisInterval}
              />
            )}

            <div className="gna-footer-container">
              <div className="grid grid-cols-3 justify-between gap-5">
                <Tooltip
                  note={
                    <p>
                      Central Electricity Regulatory Commission (CERC) Monthly
                      Report
                    </p>
                  }
                  text=" "
                >
                  <p className="gna-footer-text">Source</p>
                </Tooltip>
                <p className="gna-footer-text col-span-1 text-center">
                  Powered by <strong>GNA Energy</strong>
                </p>
                <strong className="gna-footer-text col-span-1 text-end">
                  Beta Version
                </strong>
              </div>
            </div>
          </MainPageContainer>
        </div>
      </div>
    </div>
  );
}

// ─── helpers ──────────────────────────────────────────────────────────────────
function getProducts({
  market,
  exchange,
}: {
  market: Filter[];
  exchange: Filter[];
}): Product[] {
  const PRODUCT_MAPPING: Record<string, string> = {
    DAM: "DAM",
    RTM: "RTM",
    HPDAM: "HPDAM",
    GDAM: "GDAM",
    "Intra-Day Contracts": "Intra Day",
    "Day Ahead Contingency Contracts": "DAC",
    "Daily Contracts": "Daily",
    "Weekly Contracts": "Weekly",
    "Monthly Contracts": "Monthly",
    "Any Day Single Sided Contracts": "Any Day S.S",
    Bilateral: "Bilateral",
  };

  const uniqueMarketProducts = Array.from(
    new Set(market.flatMap((m) => m.products)),
  );
  const uniqueExchangeProducts = Array.from(
    new Set(exchange.flatMap((e) => e.products)),
  );

  let common = uniqueMarketProducts.filter((mp) =>
    uniqueExchangeProducts.some((ep) => ep.id === mp.id),
  );

  if (exchange.some((e) => e.name === "Traders")) {
    common.push({ id: 13, name: "Bilateral", dname: "Bilateral" });
  }

  return common
    .map((item) => ({
      ...item,
      dname: PRODUCT_MAPPING[item.name as string] as any,
    }))
    .sort((a, b) => {
      const aName = a.dname ?? a.name;
      const bName = b.dname ?? b.name;
      return aName.localeCompare(bName);
    });
}

function getDefaultByPriceSelection(products: Product[]): number[] {
  if (products.length === 0) {
    return [];
  }

  const damProduct = products.find(
    (item) => item.name === "DAM" || item.dname === "DAM",
  );

  return [damProduct?.id ?? products[0].id];
}
